import { genericMemo } from "@redotech/react-util/component";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { getShopifyCollections } from "@redotech/redo-merchant-app-common/client/shopify";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import {
  CheckoutContextOperator,
  checkoutContextOperators,
  checkoutContextOperatorToLabel,
  checkoutContextOperatorToLabelForProducts,
  checkoutContextOperatorToLabelForTags,
  CheckoutContextPredicateExpression,
  CheckoutContextPredicateType,
  checkoutContextPredicateTypes,
  predicateTypeToLabel,
  ProductSelection,
} from "@redotech/redo-model/advanced-flow/expressions/checkout-context-predicate";
import {
  NumberComparisonOperator,
  numberComparisonOperators,
  numberComparisonToLabel,
} from "@redotech/redo-model/advanced-flow/expressions/number-expression";
import {
  StringMatchOperator,
  textMatchOperators,
} from "@redotech/redo-model/advanced-flow/expressions/text-expression";
import { Schema } from "@redotech/redo-model/advanced-flow/type-system/schema";
import { Provider } from "@redotech/redo-model/order";
import { ProductStatus } from "@redotech/redo-model/product";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTagInputField } from "@redotech/redo-web/arbiter-components/input/redo-tag-input-field";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import {
  BadgeRedoListItem,
  RedoListItem,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoTagsDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-tags-dropdown-input";
import { convertProductInfoToModalProduct } from "@redotech/redo-web/discounts/discount-selector";
import { Flex } from "@redotech/redo-web/flex";
import {
  ModalProduct,
  ModalVariants,
  ProductSelectionModalEntry,
} from "@redotech/redo-web/product/product-selection-modal";
import { Text } from "@redotech/redo-web/text";
import { UserImage, UserImageSize } from "@redotech/redo-web/user-image";
import {
  convertShopifyIdToGid,
  ShopifyIdType,
} from "@redotech/shopify-util/id";
import { filterTruthy } from "@redotech/util/array";
import { DeepPartial } from "@redotech/util/type";
import { useEffect, useMemo, useState } from "react";
import { useDebounce } from "usehooks-ts";
import { useDelayedAutoFocus } from "../common/use-delayed-auto-focus";

const predicateTypeOptions: RedoListItem<CheckoutContextPredicateType>[] =
  checkoutContextPredicateTypes.map((type) => ({
    id: type,
    type: "text",
    text: predicateTypeToLabel[type],
    value: type,
  }));

const numberComparisonOperatorOptions: RedoListItem<NumberComparisonOperator>[] =
  numberComparisonOperators.map((op) => ({
    id: op,
    type: "text",
    text: numberComparisonToLabel[op],
    value: op,
  }));

const textMatchOperatorOptions: RedoListItem<StringMatchOperator>[] =
  textMatchOperators.map((op) => ({
    id: op,
    type: "text",
    text: op.charAt(0).toUpperCase() + op.slice(1).replace(/([A-Z])/g, " $1"),
    value: op,
  }));

function isNumericPredicateType(
  predicateType: CheckoutContextPredicateType | undefined,
): boolean {
  return (
    predicateType === "cart_product_count" || predicateType === "cart_subtotal"
  );
}

function isDiscountCodePredicateType(
  predicateType: CheckoutContextPredicateType | undefined,
): boolean {
  return predicateType === "discount_code";
}

function isDiscountPercentagePredicateType(
  predicateType: CheckoutContextPredicateType | undefined,
): boolean {
  return predicateType === "discount_percentage";
}

function getOperatorOptions(
  predicateType: CheckoutContextPredicateType | undefined,
): RedoListItem<CheckoutContextOperator>[] {
  const labelMap =
    predicateType === "product_tags"
      ? checkoutContextOperatorToLabelForTags
      : predicateType === "product_membership"
        ? checkoutContextOperatorToLabelForProducts
        : checkoutContextOperatorToLabel;

  return checkoutContextOperators.map((op) => ({
    id: op,
    type: "text",
    text: labelMap[op],
    value: op,
  }));
}

export const CheckoutContextPredicateInspector = genericMemo(
  function CheckoutContextPredicateInspector({
    expression,
    setExpression,
    showValidationErrors,
  }: {
    schema: Schema;
    dataType: "Checkout Context Predicate";
    expression: DeepPartial<CheckoutContextPredicateExpression>;
    setExpression: (
      expression: DeepPartial<CheckoutContextPredicateExpression>,
    ) => void;
    showValidationErrors?: boolean;
  }) {
    const [touched, setTouched] = useState(showValidationErrors || false);

    useEffect(() => {
      if (!expression.type) {
        setExpression({
          ...expression,
          type: "checkout_context_predicate",
        } as DeepPartial<CheckoutContextPredicateExpression>);
      }
      if (!expression.operator && expression.predicateType) {
        // Set default operator based on predicate type
        if (
          isNumericPredicateType(expression.predicateType) ||
          isDiscountPercentagePredicateType(expression.predicateType)
        ) {
          setExpression({
            ...expression,
            operator: "greaterThan",
          } as DeepPartial<CheckoutContextPredicateExpression>);
        } else if (isDiscountCodePredicateType(expression.predicateType)) {
          setExpression({
            ...expression,
            operator: "equals",
          } as DeepPartial<CheckoutContextPredicateExpression>);
        } else {
          setExpression({
            ...expression,
            operator: "includesAny",
          } as DeepPartial<CheckoutContextPredicateExpression>);
        }
      }
    }, [expression, setExpression]);

    const handlePredicateTypeChange = useHandler(
      (predicateType: CheckoutContextPredicateType) => {
        // Reset the expression when changing predicate type
        if (isNumericPredicateType(predicateType)) {
          // Numeric predicates use number comparison operators
          setExpression({
            type: "checkout_context_predicate",
            field: expression.field,
            predicateType,
            operator: "greaterThan",
          } as DeepPartial<CheckoutContextPredicateExpression>);
        } else if (isDiscountCodePredicateType(predicateType)) {
          // Discount code predicates use text match operators
          setExpression({
            type: "checkout_context_predicate",
            field: expression.field,
            predicateType,
            operator: "equals",
          } as DeepPartial<CheckoutContextPredicateExpression>);
        } else if (isDiscountPercentagePredicateType(predicateType)) {
          setExpression({
            type: "checkout_context_predicate",
            field: expression.field,
            predicateType,
            operator: "greaterThan",
          } as DeepPartial<CheckoutContextPredicateExpression>);
        } else {
          // Product-based predicates use includes operators
          setExpression({
            type: "checkout_context_predicate",
            field: expression.field,
            predicateType,
            operator: "includesAny",
          } as DeepPartial<CheckoutContextPredicateExpression>);
        }
      },
    );

    const handleOperatorChange = useHandler(
      (operator: CheckoutContextOperator) => {
        setExpression({
          ...expression,
          operator,
        } as DeepPartial<CheckoutContextPredicateExpression>);
      },
    );

    const operatorOptions = useMemo(
      () => getOperatorOptions(expression.predicateType),
      [expression.predicateType],
    );

    const selectedPredicateType = predicateTypeOptions.find(
      (opt) => opt.id === expression.predicateType,
    );

    const selectedOperator = operatorOptions.find(
      (op) => op.id === expression.operator,
    );

    const predicateTypeError = useMemo(() => {
      if (!expression.predicateType && touched) {
        return "A condition type is required";
      }
      return undefined;
    }, [expression.predicateType, touched]);

    return (
      <>
        <RedoSingleSelectDropdownInput<CheckoutContextPredicateType>
          description={predicateTypeError}
          label="Condition type"
          onBlur={() => setTouched(true)}
          options={predicateTypeOptions}
          optionSelected={({ item }) => handlePredicateTypeChange(item.value)}
          placeholder="Select condition type"
          selectedItem={selectedPredicateType}
          size="xs"
          state={predicateTypeError ? "error" : undefined}
        />

        {expression.predicateType && (
          <>
            {!isNumericPredicateType(expression.predicateType) &&
              !isDiscountCodePredicateType(expression.predicateType) &&
              !isDiscountPercentagePredicateType(expression.predicateType) && (
                <RedoSingleSelectDropdownInput<CheckoutContextOperator>
                  label="Cart products"
                  options={operatorOptions}
                  optionSelected={({ item }) =>
                    handleOperatorChange(item.value)
                  }
                  selectedItem={selectedOperator}
                  size="xs"
                />
              )}

            {expression.predicateType === "collection_membership" && (
              <CollectionSelector
                collectionIds={
                  (
                    expression as DeepPartial<
                      CheckoutContextPredicateExpression & {
                        predicateType: "collection_membership";
                      }
                    >
                  ).collectionIds
                }
                setCollectionIds={(collectionIds) =>
                  setExpression({ ...expression, collectionIds })
                }
                showValidationErrors={showValidationErrors}
              />
            )}

            {expression.predicateType === "product_membership" && (
              <ProductSelector
                products={
                  (
                    expression as DeepPartial<
                      CheckoutContextPredicateExpression & {
                        predicateType: "product_membership";
                      }
                    >
                  ).products?.filter(Boolean) as
                    | DeepPartial<ProductSelection>[]
                    | undefined
                }
                setProducts={(products) =>
                  setExpression({ ...expression, products })
                }
                showValidationErrors={showValidationErrors}
              />
            )}

            {expression.predicateType === "product_tags" && (
              <TagSelector
                setTags={(tags) => setExpression({ ...expression, tags })}
                showValidationErrors={showValidationErrors}
                tags={
                  (
                    expression as DeepPartial<
                      CheckoutContextPredicateExpression & {
                        predicateType: "product_tags";
                      }
                    >
                  ).tags
                }
              />
            )}

            {isNumericPredicateType(expression.predicateType) && (
              <NumericPredicateSelector
                comparisonValue={
                  (
                    expression as DeepPartial<
                      CheckoutContextPredicateExpression & {
                        predicateType: "cart_product_count" | "cart_subtotal";
                      }
                    >
                  ).comparisonValue
                }
                operator={
                  (
                    expression as DeepPartial<
                      CheckoutContextPredicateExpression & {
                        predicateType: "cart_product_count" | "cart_subtotal";
                      }
                    >
                  ).operator as NumberComparisonOperator | undefined
                }
                predicateType={
                  expression.predicateType as
                    | "cart_product_count"
                    | "cart_subtotal"
                }
                setComparisonValue={(comparisonValue) =>
                  setExpression({
                    ...expression,
                    comparisonValue,
                  } as DeepPartial<CheckoutContextPredicateExpression>)
                }
                setOperator={(operator) =>
                  setExpression({
                    ...expression,
                    operator,
                  } as DeepPartial<CheckoutContextPredicateExpression>)
                }
                showValidationErrors={showValidationErrors}
              />
            )}

            {isDiscountCodePredicateType(expression.predicateType) && (
              <DiscountCodeSelector
                matchValues={
                  (
                    expression as DeepPartial<
                      CheckoutContextPredicateExpression & {
                        predicateType: "discount_code";
                      }
                    >
                  ).matchValues
                }
                operator={
                  (
                    expression as DeepPartial<
                      CheckoutContextPredicateExpression & {
                        predicateType: "discount_code";
                      }
                    >
                  ).operator as StringMatchOperator | undefined
                }
                setMatchValues={(matchValues) =>
                  setExpression({
                    ...expression,
                    matchValues,
                  } as DeepPartial<CheckoutContextPredicateExpression>)
                }
                setOperator={(operator) =>
                  setExpression({
                    ...expression,
                    operator,
                  } as DeepPartial<CheckoutContextPredicateExpression>)
                }
                showValidationErrors={showValidationErrors}
              />
            )}

            {isDiscountPercentagePredicateType(expression.predicateType) && (
              <DiscountPercentageSelector
                comparisonValue={
                  (
                    expression as DeepPartial<
                      CheckoutContextPredicateExpression & {
                        predicateType: "discount_percentage";
                      }
                    >
                  ).comparisonValue
                }
                operator={
                  (
                    expression as DeepPartial<
                      CheckoutContextPredicateExpression & {
                        predicateType: "discount_percentage";
                      }
                    >
                  ).operator as NumberComparisonOperator | undefined
                }
                setComparisonValue={(comparisonValue) =>
                  setExpression({
                    ...expression,
                    comparisonValue,
                  } as DeepPartial<CheckoutContextPredicateExpression>)
                }
                setOperator={(operator) =>
                  setExpression({
                    ...expression,
                    operator,
                  } as DeepPartial<CheckoutContextPredicateExpression>)
                }
                showValidationErrors={showValidationErrors}
              />
            )}
          </>
        )}
      </>
    );
  },
);

/**
 * Collection selector sub-component
 */
const CollectionSelector = genericMemo(function CollectionSelector({
  collectionIds,
  setCollectionIds,
  showValidationErrors,
}: {
  collectionIds: (string | undefined)[] | undefined;
  setCollectionIds: (ids: string[]) => void;
  showValidationErrors?: boolean;
}) {
  const merchantClient = useRequiredContext(RedoMerchantClientContext);
  const [touched, setTouched] = useState(showValidationErrors || false);
  const [searchString, setSearchString] = useState("");
  const debouncedSearch = useDebounce(searchString, 300);

  const ids = useMemo(() => collectionIds ?? [], [collectionIds]);
  const delayedAutoFocus = useDelayedAutoFocus(!ids.length);

  const collectionsLoad = useLoad(async () => {
    const collections = await getShopifyCollections(merchantClient, {
      searchString: debouncedSearch,
    });
    return collections.map((collection) => ({
      id: convertShopifyIdToGid(collection.id, ShopifyIdType.Collection),
      name: collection.name,
    }));
  }, [debouncedSearch, merchantClient]);

  const collectionOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    return (
      collectionsLoad.value?.map((collection) => ({
        type: "badge" as const,
        id: collection.id,
        value: collection.id,
        badge: { text: collection.name, color: "gray" as const },
      })) ?? []
    );
  }, [collectionsLoad.value]);

  const selectedOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    return ids
      .filter((id): id is string => id !== undefined)
      .map((id) => {
        const collection = collectionsLoad.value?.find((c) => c.id === id);
        return {
          type: "badge" as const,
          id,
          value: id,
          badge: { text: collection?.name ?? id, color: "gray" as const },
        };
      });
  }, [ids, collectionsLoad.value]);

  const handleSelectionChange = useHandler(
    (items: BadgeRedoListItem<string>[]) => {
      setCollectionIds(items.map((item) => item.value));
    },
  );

  const errorText = useMemo(() => {
    if (ids.length === 0 && touched) {
      return "At least one collection is required";
    }
    return undefined;
  }, [ids.length, touched]);

  return (
    <RedoTagsDropdownInput<string>
      autoFocus={delayedAutoFocus}
      description={errorText}
      key={delayedAutoFocus.toString()}
      label="Collections"
      loading={collectionsLoad.pending}
      onBlur={() => setTouched(true)}
      options={collectionOptions}
      placeholder="Select collections"
      searchPlaceholder="Search collections"
      searchString={searchString}
      selectedOptions={selectedOptions}
      setSearchString={setSearchString}
      setSelectedOptions={handleSelectionChange}
      size="xs"
      state={errorText ? "error" : undefined}
    />
  );
});

/**
 * Product selector sub-component using ProductSelectionModalEntry
 */
const ProductSelector = genericMemo(function ProductSelector({
  products,
  setProducts,
  showValidationErrors,
}: {
  products: DeepPartial<ProductSelection>[] | undefined;
  setProducts: (products: ProductSelection[]) => void;
  showValidationErrors?: boolean;
}) {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const [touched, setTouched] = useState(showValidationErrors || false);

  const selectedProducts = useMemo(
    () => (products ?? []).filter((p): p is ProductSelection => !!p.productId),
    [products],
  );

  const searchProductsForModal = useHandler(
    async ({
      search,
    }: {
      search: string;
      signal: AbortSignal;
    }): Promise<ModalProduct[] | undefined> => {
      if (!search.trim()) {
        return [];
      }
      const response = await rpcClient.searchProducts({
        searchString: search,
        provider: Provider.SHOPIFY,
        includeStatuses: [ProductStatus.ACTIVE, ProductStatus.DRAFT],
      });
      return (response?.products || []).map(convertProductInfoToModalProduct);
    },
  );

  // Convert selected products to the format expected by alreadySelectedProducts
  const alreadySelectedProducts = useMemo(() => {
    return selectedProducts.flatMap((p) => {
      // If no specific variants selected (all variants), we need to represent this
      // For the modal, we pass each variant that should be selected
      if (!p.variantIds || p.variantIds.length === 0) {
        // All variants - but we don't have variant data here, so pass empty
        // The modal will handle this case
        return [];
      }
      // Create a ModalProduct-like structure for each variant
      return p.variantIds.map((variantId) => ({
        product: {
          id: p.productId,
          title: p.productTitle,
          description: "",
          handle: "",
          image: { src: p.imageUrl ?? null, altText: null },
          images: [],
          tags: [],
          variants:
            p.variantIds?.map((vId, vIdx) => ({
              admin_graphql_api_id: vId,
              title: p.variantTitles?.[vIdx] ?? "",
              price: "0",
              image: { src: null, altText: null },
            })) ?? [],
        } as ModalProduct,
        variantId,
      }));
    });
  }, [selectedProducts]);

  const handleRemoveProduct = useHandler((productId: string) => {
    setProducts(selectedProducts.filter((p) => p.productId !== productId));
  });

  const handleUpdateVariants = useHandler(
    (productId: string, variantIds: string[], variantTitles: string[]) => {
      setProducts(
        selectedProducts.map((p) =>
          p.productId === productId
            ? {
                ...p,
                variantIds: variantIds.length > 0 ? variantIds : undefined,
                variantTitles:
                  variantTitles.length > 0 ? variantTitles : undefined,
              }
            : p,
        ),
      );
    },
  );

  const handleProductSelectionChange = useHandler(
    (
      selected: {
        productId: string;
        productTitle?: string;
        variantIds: string[];
        variants: ModalVariants[];
        productVariants?: ModalVariants[];
        productImage?: { src: string | null; altText: string | null };
      }[],
    ) => {
      // Convert modal selection to ProductSelection format
      const newProducts: ProductSelection[] = selected.map((item) => ({
        productId: item.productId,
        productTitle: item.productTitle ?? "",
        imageUrl: item.productImage?.src ?? undefined,
        variantIds: item.variantIds,
        variantTitles: item.variants.map((v) => v.title),
      }));

      // Merge with existing products that weren't part of this selection
      const existingProductIds = new Set(newProducts.map((p) => p.productId));
      const unchangedProducts = selectedProducts.filter(
        (p) => !existingProductIds.has(p.productId),
      );

      setProducts([...unchangedProducts, ...newProducts]);
    },
  );

  const errorText = useMemo(() => {
    if (selectedProducts.length === 0 && touched) {
      return "At least one product is required";
    }
    return undefined;
  }, [selectedProducts.length, touched]);

  return (
    <Flex dir="column" gap="md" onBlur={() => setTouched(true)}>
      <Text fontSize="xs" fontWeight="medium">
        Products
      </Text>

      {/* Selected products list */}
      {selectedProducts.map((product) => (
        <SelectedProductRow
          key={product.productId}
          onRemove={() => handleRemoveProduct(product.productId)}
          onUpdateVariants={(variantIds, variantTitles) =>
            handleUpdateVariants(product.productId, variantIds, variantTitles)
          }
          product={product}
        />
      ))}

      {/* Product selection modal entry */}
      <ProductSelectionModalEntry
        alreadySelectedProducts={alreadySelectedProducts}
        customAddProductElement={({ onAdd }) => (
          <RedoButton
            hierarchy="secondary"
            onClick={onAdd}
            size="xs"
            text="Add product"
          />
        )}
        multiselect
        productSelectionChange={handleProductSelectionChange}
        searchProducts={searchProductsForModal}
      />

      {errorText && (
        <Text fontSize="xs" textColor="error">
          {errorText}
        </Text>
      )}
    </Flex>
  );
});

/**
 * Row for displaying a selected product with variant dropdown and remove button
 */
const SelectedProductRow = genericMemo(function SelectedProductRow({
  product,
  onRemove,
  onUpdateVariants,
}: {
  product: ProductSelection;
  onRemove: () => void;
  onUpdateVariants: (variantIds: string[], variantTitles: string[]) => void;
}) {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  // Fetch product variants
  const variantsLoad = useLoad(async () => {
    // Extract numeric ID from GID
    const numericId = product.productId.split("/").pop() ?? product.productId;
    const response = await rpcClient.searchProducts({
      searchString: `id:${numericId}`,
      provider: Provider.SHOPIFY,
      includeStatuses: [ProductStatus.ACTIVE, ProductStatus.DRAFT],
    });
    const foundProduct = response?.products?.[0];
    if (!foundProduct) {
      return [];
    }
    return foundProduct.variants.map((v) => ({ id: v.id, title: v.name }));
  }, [product.productId, rpcClient]);

  const variants = useMemo(
    () => variantsLoad.value ?? [],
    [variantsLoad.value],
  );
  const hasMultipleVariants = variants.length > 1;

  // Build dropdown options from fetched variants
  const variantOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    return variants.map((v) => ({
      type: "badge" as const,
      id: v.id,
      value: v.id,
      badge: { text: v.title, color: "gray" as const },
    }));
  }, [variants]);

  // Build selected options from product's variantIds
  const selectedOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    return (product.variantIds ?? []).map((id, idx) => ({
      type: "badge" as const,
      id,
      value: id,
      badge: {
        text:
          product.variantTitles?.[idx] ??
          variants.find((v) => v.id === id)?.title ??
          id,
        color: "gray" as const,
      },
    }));
  }, [product.variantIds, product.variantTitles, variants]);

  const handleVariantSelectionChange = useHandler(
    (items: BadgeRedoListItem<string>[]) => {
      onUpdateVariants(
        items.map((item) => item.value),
        items.map(
          (item) =>
            variants.find((v) => v.id === item.value)?.title ?? item.value,
        ),
      );
    },
  );

  // Empty variantIds means "all variants match" - this is a valid state, not an error
  const matchingAllVariants =
    hasMultipleVariants &&
    (!product.variantIds || product.variantIds.length === 0);

  return (
    <Flex
      borderColor="primary"
      borderStyle="solid"
      borderWidth="1px"
      dir="column"
      gap="sm"
      p="sm"
      radius="sm"
    >
      <Flex align="center" gap="sm">
        <UserImage
          alt={product.productTitle}
          imageUrl={product.imageUrl}
          size={UserImageSize.TINY}
        />
        <Flex dir="column" grow={1}>
          <Text fontSize="xs" fontWeight="medium">
            {product.productTitle}
          </Text>
        </Flex>
        <RedoButton
          hierarchy="tertiary"
          IconLeading={TrashIcon}
          onClick={onRemove}
          size="xs"
          theme="destructive"
        />
      </Flex>

      {/* Variant dropdown for products with multiple variants */}
      {hasMultipleVariants && (
        <RedoTagsDropdownInput<string>
          description={
            matchingAllVariants ? "Matching all variants" : undefined
          }
          label="Variants"
          loading={variantsLoad.pending}
          options={variantOptions}
          placeholder="Select variants (leave empty for all)"
          selectedOptions={selectedOptions}
          setSelectedOptions={handleVariantSelectionChange}
          size="xs"
        />
      )}
    </Flex>
  );
});

/**
 * Numeric predicate selector for cart_product_count and cart_subtotal predicates.
 * Uses number comparison operators (greaterThan, lessThan, equalTo, notEqualTo).
 */
const NumericPredicateSelector = genericMemo(function NumericPredicateSelector({
  predicateType,
  operator,
  setOperator,
  comparisonValue,
  setComparisonValue,
  showValidationErrors,
}: {
  predicateType: "cart_product_count" | "cart_subtotal";
  operator: NumberComparisonOperator | undefined;
  setOperator: (operator: NumberComparisonOperator) => void;
  comparisonValue: number | undefined;
  setComparisonValue: (value: number | undefined) => void;
  showValidationErrors?: boolean;
}) {
  const [touched, setTouched] = useState(showValidationErrors || false);
  const delayedAutoFocus = useDelayedAutoFocus(comparisonValue === undefined);

  const selectedOperator = numberComparisonOperatorOptions.find(
    (item) => item.id === operator,
  );

  const errorText = useMemo(() => {
    if (comparisonValue === undefined && touched) {
      return "A value is required";
    }
    return undefined;
  }, [comparisonValue, touched]);

  const label =
    predicateType === "cart_product_count"
      ? "Product count"
      : "Subtotal amount";
  const placeholder =
    predicateType === "cart_product_count"
      ? "Enter product count"
      : "Enter amount (in store currency)";

  return (
    <>
      <RedoSingleSelectDropdownInput<NumberComparisonOperator>
        label="Comparison"
        options={numberComparisonOperatorOptions}
        optionSelected={({ item }) => setOperator(item.value)}
        selectedItem={selectedOperator}
        size="xs"
      />
      <RedoTextInput
        autoFocus={delayedAutoFocus}
        description={errorText}
        inputTypeHint="number"
        key={delayedAutoFocus.toString()}
        label={label}
        onBlur={() => setTouched(true)}
        placeholder={placeholder}
        setValue={(value) => {
          const numValue = value === "" ? undefined : parseFloat(value);
          setComparisonValue(Number.isNaN(numValue) ? undefined : numValue);
        }}
        size="sm"
        state={errorText ? "error" : "default"}
        value={comparisonValue?.toString() ?? ""}
      />
    </>
  );
});

/**
 * Tag selector sub-component with search/autocomplete
 */
const TagSelector = genericMemo(function TagSelector({
  tags,
  setTags,
  showValidationErrors,
}: {
  tags: (string | undefined)[] | undefined;
  setTags: (tags: string[]) => void;
  showValidationErrors?: boolean;
}) {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const [touched, setTouched] = useState(showValidationErrors || false);
  const [searchString, setSearchString] = useState("");
  const debouncedSearch = useDebounce(searchString, 300);

  const tagList = useMemo(() => tags ?? [], [tags]);
  const delayedAutoFocus = useDelayedAutoFocus(!tagList.length);

  // Fetch tags from Shopify with search
  const tagsLoad = useLoad(async () => {
    const response = await rpcClient.searchProductTags({
      searchString: debouncedSearch,
      provider: Provider.SHOPIFY,
    });
    return response.tags ?? [];
  }, [debouncedSearch, rpcClient]);

  // Build options from fetched tags plus selected tags
  const tagOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    const fetchedTags = tagsLoad.value ?? [];
    const selectedTagSet = new Set(tagList.filter(Boolean) as string[]);

    // Combine fetched tags with selected tags to ensure selected ones appear
    const allTags = new Set([...fetchedTags, ...selectedTagSet]);

    return Array.from(allTags).map((tag) => ({
      type: "badge" as const,
      id: tag,
      value: tag,
      badge: { text: tag, color: "gray" as const },
    }));
  }, [tagsLoad.value, tagList]);

  const selectedOptions: BadgeRedoListItem<string>[] = useMemo(() => {
    return tagList
      .filter((tag): tag is string => tag !== undefined)
      .map((tag) => ({
        type: "badge" as const,
        id: tag,
        value: tag,
        badge: { text: tag, color: "gray" as const },
      }));
  }, [tagList]);

  const handleSelectionChange = useHandler(
    (items: BadgeRedoListItem<string>[]) => {
      setTags(items.map((item) => item.value));
    },
  );

  const errorText = useMemo(() => {
    if (tagList.length === 0 && touched) {
      return "At least one tag is required";
    }
    return undefined;
  }, [tagList.length, touched]);

  return (
    <RedoTagsDropdownInput<string>
      autoFocus={delayedAutoFocus}
      description={errorText}
      key={delayedAutoFocus.toString()}
      label="Tags"
      loading={tagsLoad.pending}
      onBlur={() => setTouched(true)}
      options={tagOptions}
      placeholder="Select tags"
      searchPlaceholder="Search product tags"
      searchString={searchString}
      selectedOptions={selectedOptions}
      setSearchString={setSearchString}
      setSelectedOptions={handleSelectionChange}
      size="xs"
      state={errorText ? "error" : undefined}
    />
  );
});

/**
 * Discount code selector sub-component.
 * Uses text match operators with case-insensitive matching.
 * Supports multiple match values (matches if ANY value matches).
 */
const DiscountCodeSelector = genericMemo(function DiscountCodeSelector({
  operator,
  setOperator,
  matchValues,
  setMatchValues,
  showValidationErrors,
}: {
  operator: StringMatchOperator | undefined;
  setOperator: (operator: StringMatchOperator) => void;
  matchValues: (string | undefined)[] | undefined;
  setMatchValues: (values: string[]) => void;
  showValidationErrors?: boolean;
}) {
  const [touched, setTouched] = useState(showValidationErrors || false);
  const [tagInputValue, setTagInputValue] = useState("");

  const tags = useMemo(() => {
    return filterTruthy(
      matchValues?.map((value) => (value ? { text: value } : undefined)) ?? [],
    );
  }, [matchValues]);

  const delayedAutoFocus = useDelayedAutoFocus(!tags.length);

  const selectedOperator = textMatchOperatorOptions.find(
    (item) => item.id === operator,
  );

  const errorText = useMemo(() => {
    if ((!matchValues || matchValues.length === 0) && touched) {
      return tagInputValue
        ? "Press enter to add the value"
        : "At least one value is required";
    }
    return undefined;
  }, [matchValues, touched, tagInputValue]);

  return (
    <>
      <RedoSingleSelectDropdownInput<StringMatchOperator>
        label="Text match type"
        options={textMatchOperatorOptions}
        optionSelected={({ item }) => setOperator(item.value)}
        selectedItem={selectedOperator}
        size="xs"
      />
      <RedoTagInputField
        autoFocus={delayedAutoFocus}
        description={errorText ?? "Comparison is case-insensitive"}
        fitContent
        inputValue={tagInputValue}
        key={delayedAutoFocus.toString()}
        label="Values"
        onBlur={() => setTouched(true)}
        setInputValue={setTagInputValue}
        setTags={(newTags) => setMatchValues(newTags)}
        state={errorText ? "error" : "default"}
        tags={tags}
      />
    </>
  );
});

/**
 * Discount percentage selector sub-component.
 * Compares effective discount percentage (computed from cart line prices and subtotal).
 */
const DiscountPercentageSelector = genericMemo(
  function DiscountPercentageSelector({
    operator,
    setOperator,
    comparisonValue,
    setComparisonValue,
    showValidationErrors,
  }: {
    operator: NumberComparisonOperator | undefined;
    setOperator: (operator: NumberComparisonOperator) => void;
    comparisonValue: number | undefined;
    setComparisonValue: (value: number | undefined) => void;
    showValidationErrors?: boolean;
  }) {
    const [touched, setTouched] = useState(showValidationErrors || false);
    const delayedAutoFocus = useDelayedAutoFocus(comparisonValue === undefined);

    const selectedOperator = numberComparisonOperatorOptions.find(
      (item) => item.id === operator,
    );

    const errorText = useMemo(() => {
      if (comparisonValue === undefined && touched) {
        return "A value is required";
      }
      return undefined;
    }, [comparisonValue, touched]);

    return (
      <>
        <RedoSingleSelectDropdownInput<NumberComparisonOperator>
          label="Comparison"
          options={numberComparisonOperatorOptions}
          optionSelected={({ item }) => setOperator(item.value)}
          selectedItem={selectedOperator}
          size="xs"
        />
        <RedoTextInput
          autoFocus={delayedAutoFocus}
          description={errorText}
          inputTypeHint="number"
          key={delayedAutoFocus.toString()}
          label="Percentage value"
          onBlur={() => setTouched(true)}
          placeholder="Enter percentage (e.g., 10 for 10%)"
          setValue={(value) => {
            const numValue = value === "" ? undefined : parseFloat(value);
            setComparisonValue(Number.isNaN(numValue) ? undefined : numValue);
          }}
          size="sm"
          state={errorText ? "error" : "default"}
          value={comparisonValue?.toString() ?? ""}
        />
      </>
    );
  },
);
